import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SoluuDevice, WiFiStatus } from '@soluu/ble-core';

import { appendTransition, deriveResult } from '../core/attempt.js';
import { buildLogEntry } from '../core/logEntry.js';
import { newId } from '../core/uuid.js';
import type {
  AttemptAnomaly,
  LogEntry,
  RunMode,
  ScenarioConfigSnapshot,
  Transition,
} from '../core/types.js';
import { useAttemptIdRef } from '../state/AttemptIdContext.js';
import { useDiagnostics } from '../state/DiagnosticsProvider.js';
import { useLogs } from '../state/LogsProvider.js';

interface AttemptState {
  readonly id: string;
  readonly startedAtMs: number;
  readonly transitions: readonly Transition[];
  readonly mode: RunMode;
  readonly scenario: string | null;
  readonly scenarioConfig: ScenarioConfigSnapshot | null;
  readonly ssid: string;
  readonly password: string;
  /** Anomalies noted from outside the status stream — see `noteAnomaly`. */
  readonly extraAnomalies: readonly AttemptAnomaly[];
}

export interface StartAttemptParams {
  readonly mode: RunMode;
  readonly scenario: string | null;
  readonly scenarioConfig: ScenarioConfigSnapshot | null;
  readonly ssid: string;
  readonly password: string;
}

/**
 * Owns a single in-flight Connect attempt. Subscribes to live WiFiStatus
 * updates from the device and accumulates transitions; when the derived
 * result becomes terminal (pass/fail/timeout) the attempt is finalized into
 * a LogEntry and pushed to the LogsContext.
 *
 * `start()` returns a promise that resolves with the finalized LogEntry —
 * the suite runner uses this to await each scenario without polling the
 * stale closure of `inFlight`.
 *
 * One attempt at a time per SPEC §"Out of Scope" (single-device harness).
 */
export function useAttempt(device: SoluuDevice | null, status: WiFiStatus | null) {
  const { addEntry } = useLogs();
  const diagnostics = useDiagnostics();
  const attemptIdRef = useAttemptIdRef();
  const [attempt, setAttempt] = useState<AttemptState | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const resolverRef = useRef<((entry: LogEntry) => void) | null>(null);
  // Dedup key for "have we already processed this exact status update?"
  // Use a value-based key (timestamp + raw byte) instead of object reference
  // equality. Reference equality looked correct but failed in the wild after
  // a GATT reconnect: notifications arrived (visible in diagnostics) but no
  // transition:appended events fired, suggesting a stale ref or subtle React
  // batching issue we couldn't isolate from event logs alone. A value key
  // can't be fooled by reference shenanigans — two notifications from the
  // device are only "the same event" if they share both timestamp AND raw.
  const lastSeenStatusKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!attempt || !status) return;
    // Skip if we already processed this exact status update.
    const key = `${status.timestamp}-${status.raw}`;
    if (lastSeenStatusKeyRef.current === key) return;
    // Skip if the status predates the attempt. The Soluu's last WiFi-status
    // notification is held in useSoluuDevice's state for the lifetime of the
    // GATT connection, so a fresh attempt would otherwise inherit the prior
    // run's terminal label (e.g. ON_INTERNET → instant false pass, or
    // CANNOT_CONNECT → instant false fail at tMs=0) before any new
    // notification arrives. Compare timestamps rather than reference because
    // the device may not have published anything new yet between attempts.
    if (status.timestamp < attempt.startedAtMs) return;
    lastSeenStatusKeyRef.current = key;
    // Compute outcome and emit diagnostics OUTSIDE setState. State updaters
    // must be pure — StrictMode runs them twice in dev to surface impurity,
    // and concurrent rendering can re-invoke them in production. Emitting
    // diagnostics here was producing duplicate `transition:appended` /
    // `:deduped` rows in the panel even though the timeline itself dedupes.
    const next = appendTransition(attempt.transitions, status, attempt.startedAtMs);
    const appended = next.length > attempt.transitions.length;
    diagnostics.pushEvent({
      attemptId: attempt.id,
      source: 'attempt',
      type: appended ? 'transition:appended' : 'transition:deduped',
      payload: {
        label: status.label,
        raw: status.raw,
        tMs: Math.max(0, Math.round(status.timestamp - attempt.startedAtMs)),
      },
    });
    if (status.label === 'ON_NETWORK') {
      diagnostics.pushEvent({
        attemptId: attempt.id,
        source: 'attempt',
        type: 'attempt:on-network-observed',
        payload: { raw: status.raw },
      });
    }
    if (appended) {
      setAttempt({ ...attempt, transitions: next });
    }
  }, [status, attempt, diagnostics]);

  useEffect(() => {
    if (!attempt) return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [attempt]);

  const derived = useMemo(() => {
    if (!attempt) return null;
    return deriveResult(attempt.transitions, {
      nowMs: now,
      startedAtMs: attempt.startedAtMs,
    });
  }, [attempt, now]);

  useEffect(() => {
    if (!attempt || !device || !derived || derived.result === 'pending') return;
    const entry = buildLogEntry({
      id: attempt.id,
      startedAtMs: attempt.startedAtMs,
      mode: attempt.mode,
      scenario: attempt.scenario,
      scenarioConfig: attempt.scenarioConfig,
      device: {
        // ble-core widened these to `string | null` in Stage 1a (null =
        // could not read, '' = device reported empty). DeviceSnapshot stays
        // `string` so the LogEntry schema doesn't move; a null collapses to
        // the empty string the schema already used for "unknown".
        firmwareVersion: device.firmwareVersion ?? '',
        radioFirmwareVersion: device.radioFirmwareVersion ?? '',
        macAddressHint: device.mac,
      },
      wifi: { ssid: attempt.ssid, passwordLength: attempt.password.length },
      transitions: attempt.transitions,
      derived,
      extraAnomalies: attempt.extraAnomalies,
    });
    addEntry(entry);
    diagnostics.pushEvent({
      attemptId: attempt.id,
      source: 'attempt',
      type: 'attempt:end',
      payload: {
        result: entry.result,
        failureReason: entry.failureReason,
        transitionsCount: attempt.transitions.length,
        totalDurationMs: entry.totalDurationMs,
      },
    });
    attemptIdRef.current = null;
    setAttempt(null);
    resolverRef.current?.(entry);
    resolverRef.current = null;
  }, [attempt, derived, device, addEntry, diagnostics, attemptIdRef]);

  const start = useCallback(
    (params: StartAttemptParams): Promise<LogEntry> => {
      return new Promise<LogEntry>((resolve) => {
        resolverRef.current = resolve;
        const id = newId();
        attemptIdRef.current = id;
        // Mark whatever the device is currently holding as already-seen so the
        // first effect tick after this start() doesn't try to process it. The
        // timestamp guard in the effect is the authoritative defense; this
        // ref-pin is belt-and-suspenders against same-tick races.
        lastSeenStatusKeyRef.current = status
          ? `${status.timestamp}-${status.raw}`
          : null;
        diagnostics.pushEvent({
          attemptId: id,
          source: 'attempt',
          type: 'attempt:start',
          payload: {
            mode: params.mode,
            scenario: params.scenario,
            ssid: params.ssid,
            deviceName: device?.name ?? null,
          },
        });
        setAttempt({
          id,
          startedAtMs: Date.now(),
          transitions: [],
          extraAnomalies: [],
          ...params,
        });
      });
    },
    [attemptIdRef, device, diagnostics, status],
  );

  /**
   * Record an anomaly observed outside the WiFi-status transition stream
   * (e.g. a stored-credential read that failed despite the characteristic
   * claiming read support). A clean no-op when no attempt is in flight — the
   * functional updater simply returns the prior (null) state. `tMs` is filled
   * relative to the in-flight attempt's start.
   */
  const noteAnomaly = useCallback((input: Omit<AttemptAnomaly, 'tMs'>) => {
    setAttempt((prev) =>
      prev
        ? {
            ...prev,
            extraAnomalies: [
              ...prev.extraAnomalies,
              { ...input, tMs: Math.max(0, Date.now() - prev.startedAtMs) },
            ],
          }
        : prev,
    );
  }, []);

  const reset = useCallback(() => {
    attemptIdRef.current = null;
    lastSeenStatusKeyRef.current = null;
    setAttempt(null);
    resolverRef.current = null;
  }, [attemptIdRef]);

  return {
    transitions: attempt?.transitions ?? [],
    startedAtMs: attempt?.startedAtMs ?? null,
    derived,
    inFlight: attempt !== null && derived?.result === 'pending',
    start,
    reset,
    noteAnomaly,
  };
}
